import { getAllinfo } from "@/api/user";
import { useSystemConfigStore } from "@/stores/systemConfig";
import { useUserInfoStore } from "@/stores/userInfo";
import { createRouter, createWebHashHistory } from "vue-router";

// 路由预加载缓存
const preloadCache: Record<string, boolean> = {};
// 首屏关键路由
const criticalRoutes = ["home"];

// 智能预加载函数（带缓存）
function smartPreload(routeName: string) {
  if (!routeName || preloadCache[routeName]) return;

  const route = router.options.routes.find((r) => r.name === routeName);
  if (route && typeof route.component === "function") {
    (route.component as () => Promise<any>)().then(() => {
      preloadCache[routeName] = true;
    });
  }
}

// 智能预加载关键路由（首屏相关）
function preloadCriticalRoutes() {
  criticalRoutes.forEach((route) => smartPreload(route));
}

// 空闲时预加载非关键路由
function idlePreloadNonCriticalRoutes() {
  // 收集非关键异步路由
  const nonCriticalRoutes = router.options.routes
    .filter(
      (route) =>
        typeof route.component === "function" &&
        !criticalRoutes.includes(route.name as string)
    )
    .map((route) => route.name as string);

  // 浏览器空闲时执行
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => {
      nonCriticalRoutes.forEach((route: string) => smartPreload(route));
    });
  } else {
    // 兼容方案
    setTimeout(
      () => nonCriticalRoutes.forEach((route: string) => smartPreload(route)),
      5000
    );
  }
}

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/views/home/index.vue"),
      meta: {
        layout: "marketing",
        title: "首页",
        description:
          "乐兔智能 Letoo AI：不止给建议，帮你把 AI 真正跑进业务里。FDE 交付，最快 2 周见效。",
      },
    },
    {
      path: "/case",
      name: "case",
      component: () => import("@/views/case/index.vue"),
      meta: {
        layout: "marketing",
        title: "客户案例",
        description:
          "跨境电商、社媒、供应链、法律企服财税四大行业的 AI 落地案例，明确周期与量化 ROI。",
      },
    },
    {
      path: "/solutions",
      name: "solutions",
      component: () => import("@/views/solutions/index.vue"),
      meta: {
        layout: "marketing",
        title: "解决方案",
        description:
          "四大行业 AI 落地解决方案：按场景定制 Agent，明确周期、量化 ROI。",
      },
    },
    {
      path: "/about",
      name: "about",
      component: () => import("@/views/about/index.vue"),
      meta: {
        layout: "marketing",
        title: "关于我们",
        description: "乐兔智能 Letoo AI：AI 落地行动派，最后一公里的铺路人。",
      },
    },
    {
      path: "/fde",
      name: "fde",
      component: () => import("@/views/fde/index.vue"),
      meta: {
        layout: "marketing",
        title: "FDE 交付",
        description: "前沿部署工程师交付，从业务诊断到私有化上线，专治 AI 落地最后一公里。",
      },
    },
    {
      path: "/courses",
      name: "courses",
      component: () => import("@/views/courses/index.vue"),
      meta: {
        layout: "marketing",
        title: "课程",
        description: "乐兔智能 Letoo AI FDE 课程：个人职业培训、企业 AI 落地与 Agent 工程专题。",
      },
    },
    {
      path: "/community",
      name: "community",
      component: () => import("@/views/community/index.vue"),
      meta: {
        layout: "marketing",
        title: "开发者社群",
        description: "加入乐兔智能 Letoo AI 开发者社群。",
      },
    },
    {
      path: "/auth",
      name: "auth",
      component: () => import("@/views/auth/index.vue"),
      meta: {
        title: "登录",
      },
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: () => import("@/views/dashboard/index.vue"),
      meta: {
        title: "仪表盘",
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/not-found/index.vue"),
      meta: { title: "404 - Page Not Found" },
    },
  ],
  scrollBehavior: (to, from, savedPosition) => {
    return { left: 0, top: 0 };
  },
});

let catchCount = 0;

/**获取app config */
async function getAppConfig() {
  const systemConfigStore = useSystemConfigStore();
  return new Promise((resolve, reject) => {
    getAllinfo()
      .then(({ success, resultMap }: any) => {
        if (success) {
          systemConfigStore.setBaseConfig(resultMap);
          import.meta.env.VITE_MODE !== "production" &&
            console.log("app config", systemConfigStore.getBaseConfig);
          return resolve({ success, resultMap });
        } else {
          return Promise.reject();
        }
      })
      .catch((err) => {
        console.log(err, "err");
        if (catchCount < 10) {
          catchCount++;
          return setTimeout(() => {
            getAppConfig();
          }, 3000);
        }
      });
  });
}

// 应用启动后立即预加载关键路由
preloadCriticalRoutes();

// 空闲时预加载非关键路由
idlePreloadNonCriticalRoutes();

let firstEnterFlag = true;
router.beforeEach(async (to, from, next) => {
  const systemConfigStore = useSystemConfigStore();
  const userInfoStore = useUserInfoStore();

  if (firstEnterFlag) {
    if (
      (!userInfoStore.isLogin && to.path === "/") ||
      systemConfigStore.getBaseConfig
    ) {
      getAppConfig();
    } else {
      await getAppConfig();
    }
    firstEnterFlag = false;
  }

  if (!userInfoStore.isLogin && to.path === "/dashboard") {
    next({ path: "/auth", replace: true });
  } else {
    next();
  }
});

router.afterEach((to) => {
  const brand = "乐兔智能 Letoo AI";
  const title = (to.meta?.title as string) || "";
  document.title = title ? `${title} · ${brand}` : brand;

  const desc = (to.meta?.description as string) || "";
  let el = document.querySelector(
    'meta[name="description"]'
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.name = "description";
    document.head.appendChild(el);
  }
  el.content = desc;
});

export default router;
